// src/services/learnerService.js (Enhanced with profile images)
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  getDoc,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, COLLECTIONS } from './firebase';
import { auth } from './firebase';

// Extend collections for learner management
export const LEARNER_COLLECTIONS = {
  ...COLLECTIONS,
  LEARNERS: 'taskdata-learners',
  LEARNER_DOCUMENTS: 'taskdata-learner-documents'
};

export class LearnerService {
  // Get current user ID
  static getCurrentUserId() {
    return auth.currentUser?.uid;
  }

  // Check if user is authenticated
  static ensureAuthenticated() {
    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    return userId;
  }

  static async getUserProfile() {
    const userId = this.ensureAuthenticated();
    
    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
      return userDoc.exists() ? userDoc.data() : null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  // Upload profile image
  static async uploadProfileImage(learnerId, file) {
    this.ensureAuthenticated();

    try {
      // Validate file
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        throw new Error('Please upload only JPEG, PNG, or WebP images');
      }

      if (file.size > maxSize) {
        throw new Error('Image size must be less than 5MB');
      }

      // Create storage reference
      const timestamp = Date.now();
      const fileName = `profile_${timestamp}.${file.type.split('/')[1]}`;
      const storageRef = ref(storage, `learner-profiles/${learnerId}/${fileName}`);
      
      // Upload with metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'uploadedBy': this.getCurrentUserId(),
          'uploadedAt': new Date().toISOString(),
          'originalName': file.name
        }
      };
      
      const snapshot = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        path: snapshot.ref.fullPath,
        fileName: fileName
      };
    } catch (error) {
      console.error('Error uploading profile image:', error);
      throw new Error(error.message || 'Failed to upload profile image');
    }
  }

  // Delete profile image
  static async deleteProfileImage(imagePath) {
    this.ensureAuthenticated();

    try {
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
    } catch (error) {
      console.error('Error deleting profile image:', error);
      throw new Error('Failed to delete profile image');
    }
  }

  // Create a single learner
  static async createLearner(learnerData, profileImageFile = null) {
    const userId = this.ensureAuthenticated();

    try {
      // First create the learner document to get an ID
      const docRef = await addDoc(collection(db, LEARNER_COLLECTIONS.LEARNERS), {
        ...learnerData,
        createdBy: userId,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'active',
        profileImageUrl: null,
        profileImagePath: null
      });

      const learnerId = docRef.id;

      // Upload profile image if provided
      if (profileImageFile) {
        try {
          const imageResult = await this.uploadProfileImage(learnerId, profileImageFile);
          
          // Update learner with profile image info
          await updateDoc(docRef, {
            profileImageUrl: imageResult.url,
            profileImagePath: imageResult.path,
            updatedAt: new Date()
          });
        } catch (imageError) {
          console.error('Error uploading profile image:', imageError);
          // Don't fail the whole operation, just log the error
        }
      }

      return learnerId;
    } catch (error) {
      console.error('Error creating learner:', error);
      throw new Error('Failed to create learner');
    }
  }

  // Update learner with optional profile image
  static async updateLearner(learnerId, updates, profileImageFile = null) {
    this.ensureAuthenticated();

    try {
      let updateData = { ...updates, updatedAt: new Date() };

      // Handle profile image update
      if (profileImageFile) {
        // Get current learner data to potentially delete old image
        const currentDoc = await getDoc(doc(db, LEARNER_COLLECTIONS.LEARNERS, learnerId));
        const currentData = currentDoc.data();

        // Upload new profile image
        const imageResult = await this.uploadProfileImage(learnerId, profileImageFile);
        
        updateData.profileImageUrl = imageResult.url;
        updateData.profileImagePath = imageResult.path;

        // Delete old profile image if it exists
        if (currentData?.profileImagePath) {
          try {
            await this.deleteProfileImage(currentData.profileImagePath);
          } catch (deleteError) {
            console.error('Error deleting old profile image:', deleteError);
          }
        }
      }

      const docRef = doc(db, LEARNER_COLLECTIONS.LEARNERS, learnerId);
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating learner:', error);
      throw new Error('Failed to update learner');
    }
  }

  // Batch create learners from CSV
  static async createLearnersFromCSV(learnersData) {
    const userId = this.ensureAuthenticated();
    const batch = writeBatch(db);
    const results = { success: 0, errors: [] };

    try {
      for (let i = 0; i < learnersData.length; i++) {
        const learnerData = learnersData[i];
        
        // Validate required fields
        if (!learnerData.surname || !learnerData.firstName) {
          results.errors.push(`Row ${i + 1}: Surname and First Name are required`);
          continue;
        }

        const learnerRef = doc(collection(db, LEARNER_COLLECTIONS.LEARNERS));
        batch.set(learnerRef, {
          ...learnerData,
          createdBy: userId,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 'active',
          importedAt: new Date(),
          importBatch: Date.now(),
          profileImageUrl: null,
          profileImagePath: null
        });

        results.success++;
      }

      if (results.success > 0) {
        await batch.commit();
      }

      return results;
    } catch (error) {
      console.error('Error batch creating learners:', error);
      throw new Error('Failed to import learners');
    }
  }

  // Get all learners
  static async getLearners(grade = null, subject = null) {
    const userId = this.ensureAuthenticated();

    try {
      let queries = [];
      
      // Get user profile to determine school/project context
      const userProfile = await this.getUserProfile(); 
      
      // Query 1: Own learners (existing behavior)
      let ownQuery = query(
        collection(db, COLLECTIONS.LEARNERS),
        where('createdBy', '==', userId),
        orderBy('learnerSurname', 'asc')
      );
      
      if (grade) ownQuery = query(ownQuery, where('grade', '==', grade));
      if (subject) ownQuery = query(ownQuery, where('subject', '==', subject));
      
      queries.push(ownQuery);
      
      // Query 2: Same school learners (if school info available)
      if (userProfile?.schoolName) {
        let schoolQuery = query(
          collection(db, COLLECTIONS.LEARNERS),
          where('school', '==', userProfile.schoolName),
          orderBy('learnerSurname', 'asc')
        );
        
        if (grade) schoolQuery = query(schoolQuery, where('grade', '==', grade));
        if (subject) schoolQuery = query(schoolQuery, where('subject', '==', subject));
        
        queries.push(schoolQuery);
      }
      
      // Query 3: Same project learners (if project info available)  
      if (userProfile?.projectName) {
        let projectQuery = query(
          collection(db, COLLECTIONS.LEARNERS),
          where('project', '==', userProfile.projectName),
          orderBy('learnerSurname', 'asc')
        );
        
        if (grade) projectQuery = query(projectQuery, where('grade', '==', grade));
        if (subject) projectQuery = query(projectQuery, where('subject', '==', subject));
        
        queries.push(projectQuery);
      }
      
      // Query 4: Detailed learners from school
      if (userProfile?.schoolName) {
        let detailedSchoolQuery = query(
          collection(db, 'taskdata-learners'),
          where('schoolName', '==', userProfile.schoolName)
        );
        
        queries.push(detailedSchoolQuery);
      }
      
      // Query 5: Detailed learners from project
      if (userProfile?.projectName) {
        let detailedProjectQuery = query(
          collection(db, 'taskdata-learners'),
          where('projectName', '==', userProfile.projectName)
        );
        
        queries.push(detailedProjectQuery);
      }

      // Execute all queries and combine results with proper deduplication
      const allLearners = new Map();
      const seenLearners = new Set(); // Track unique learners by personal details
      
      for (const q of queries) {
        try {
          const querySnapshot = await getDocs(q);
          querySnapshot.docs.forEach(doc => {
            const data = doc.data();
            
            // Create learner identity key (normalized)
            const surname = (data.learnerSurname || data.surname || '').trim().toLowerCase();
            const firstName = (data.learnerName || data.firstName || '').trim().toLowerCase();
            const identityNumber = (data.identityNumber || '').trim();
            
            // Create unique identity key for the person (not document)
            const personKey = identityNumber ? 
              `id_${identityNumber}` : 
              `name_${surname}_${firstName}`;
            
            // Skip if we've already processed this person
            if (seenLearners.has(personKey)) {
              return;
            }
            
            seenLearners.add(personKey);
            
            // Create consistent learner object
            const learner = {
              id: doc.id, // Keep original document ID
              // Map all field variations consistently
              learnerSurname: data.learnerSurname || data.surname || '',
              learnerName: data.learnerName || data.firstName || '',
              surname: data.surname || data.learnerSurname || '',
              firstName: data.firstName || data.learnerName || '',
              secondName: data.secondName || '',
              school: data.school || data.schoolName || '',
              schoolName: data.schoolName || data.school || '',
              project: data.project || data.projectName || '',
              projectName: data.projectName || data.project || '',
              grade: data.grade || '',
              subject: data.subject || '',
              gender: data.gender || '',
              identityNumber: data.identityNumber || '',
              mobileNumber: data.mobileNumber || '',
              email: data.email || '',
              physicalAddress: data.physicalAddress || '',
              dateOfBirth: data.dateOfBirth || '',
              parentGuardianName: data.parentGuardianName || '',
              parentGuardianType: data.parentGuardianType || '',
              parentMobile: data.parentMobile || '',
              relationship: data.relationship || '',
              profileImageUrl: data.profileImageUrl || null,
              enrolledSubjects: data.enrolledSubjects || [],
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
              createdBy: data.createdBy || '',
              status: data.status || 'active',
              // Track which collection this came from for debugging
              sourceCollection: doc.ref.parent.id
            };
            
            // Use document ID as the map key to ensure unique entries
            allLearners.set(doc.id, learner);
          });
        } catch (error) {
          console.warn('Error executing learner query:', error);
        }
      }

      // Convert to array and sort
      return Array.from(allLearners.values())
        .filter(learner => learner.status === 'active') // Only show active learners
        .sort((a, b) => {
          const surnameA = a.learnerSurname || a.surname || '';
          const surnameB = b.learnerSurname || b.surname || '';
          return surnameA.localeCompare(surnameB);
        });

    } catch (error) {
      console.error('Error fetching learners:', error);
      throw new Error('Failed to fetch learners');
    }
  }

  static async getLearnersByGradeAndSubject(grade = null, subject = null) {
    const userId = this.ensureAuthenticated();

    try {
      // Build query constraints
      let constraints = [
        where('createdBy', '==', userId),
        orderBy('createdAt', 'desc')
      ];

      // Add grade filter if specified
      if (grade) {
        constraints.push(where('grade', '==', grade));
      }

      const q = query(
        collection(db, COLLECTIONS.LEARNERS),
        ...constraints
      );

      const querySnapshot = await getDocs(q);
      let learners = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));

      // Filter by subject if specified (since subjects might be in array)
      if (subject) {
        learners = learners.filter(learner => {
          const learnerSubjects = learner.subjects || learner.enrolledSubjects || [];
          return Array.isArray(learnerSubjects) 
            ? learnerSubjects.includes(subject)
            : learnerSubjects === subject || learner.subject === subject;
        });
      }

      console.log(`Found ${learners.length} learners for grade ${grade}, subject ${subject}`);
      return learners;

    } catch (error) {
      console.error('Error getting learners by grade and subject:', error);
      throw new Error('Failed to fetch learners');
    }
  }

  // Get single learner
  static async getLearner(id) {
    try {
      const docRef = doc(db, COLLECTIONS.LEARNERS, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          // Ensure enrolledSubjects is always an array
          enrolledSubjects: data.enrolledSubjects && Array.isArray(data.enrolledSubjects) 
            ? data.enrolledSubjects 
            : ['']
        };
      } else {
        throw new Error('Learner not found');
      }
    } catch (error) {
      console.error('Error fetching learner:', error);
      throw error;
    }
  }

  // Soft delete learner
  static async deleteLearner(learnerId) {
    this.ensureAuthenticated();

    try {
      const docRef = doc(db, LEARNER_COLLECTIONS.LEARNERS, learnerId);
      await updateDoc(docRef, {
        status: 'deleted',
        deletedAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error deleting learner:', error);
      throw new Error('Failed to delete learner');
    }
  }

  // Upload learner document
  static async uploadLearnerDocument(learnerId, file, documentType) {
    this.ensureAuthenticated();

    try {
      const fileRef = ref(storage, `learner-documents/${learnerId}/${documentType}/${file.name}`);
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Save document reference in Firestore
      await addDoc(collection(db, LEARNER_COLLECTIONS.LEARNER_DOCUMENTS), {
        learnerId,
        documentType,
        fileName: file.name,
        fileUrl: downloadURL,
        filePath: snapshot.ref.fullPath,
        uploadedAt: new Date()
      });

      return {
        url: downloadURL,
        path: snapshot.ref.fullPath
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw new Error('Failed to upload document');
    }
  }

  // Parse CSV for learner import
  static parseCSVContent(csvContent) {
    const lines = csvContent.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must contain header row and at least one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const learners = [];

    // Expected headers mapping
    const headerMap = {
      'surname': 'surname',
      'first name': 'firstName',
      'second name': 'secondName',
      'gender': 'gender',
      'grade': 'grade',
      'identity number': 'identityNumber',
      'mobile number': 'mobileNumber',
      'email address': 'email',
      'physical address': 'physicalAddress',
      'parent/guardian type': 'parentGuardianType',
      'parent/guardian name': 'parentGuardianName',
      'parent mobile': 'parentMobile',
      'parent/guardian email': 'parentGuardianEmail',
      'relationship': 'relationship',
      'name of school': 'schoolName',
      'school tel': 'schoolTel',
      'school email': 'schoolEmail',
      'project name': 'projectName',
      'enrolled subjects': 'enrolledSubjects'
    };

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const learner = {};

      headers.forEach((header, index) => {
        const mappedField = headerMap[header];
        if (mappedField && values[index]) {
          if (mappedField === 'dateOfBirth') {
            learner[mappedField] = new Date(values[index]);
          } else if (mappedField === 'enrolledSubjects') {
            learner[mappedField] = values[index].split(';').map(s => s.trim()).filter(s => s);
          } else {
            learner[mappedField] = values[index];
          }
        }
      });

      if (learner.surname && learner.firstName) {
        learners.push(learner);
      }
    }

    return learners;
  }
}